<script lang="ts">
    import { setContext } from "svelte";
    import { writable } from "svelte/store";
    import Button from "@/lib/components/ui/button/button.svelte";
    import { CyclingProgress } from "@/Common";
    import { MyFitnessStores } from "@/MyFitness/stores";
    import XfaiProgramPage from "@/Program/XfaiProgramPage.svelte";
    import FirstTimeGoalForm from "@/Program/FirstTimeGoalForm.svelte";
    import { ProgramType, maxWeeklyHoursBounds } from "@/Common/constants";
    import { baseRequest, createGoalPlan, waitForForecastWorkouts } from "@/Util/utils";
    import route from "@/Util/routes";

    /*
     * "Plan your Training" step of the account setup wizard: generates a Forecast AI goal plan for the user's
     * freshly analyzed fitness signature, and saves it.
     */

    /** Response of wizard/forecast-data */
    export let data: {
        training_advice: any,
        training_plan: any,
        user_params: any,
        cal_settings: any,
        training_medians: any,
        no_data: boolean,
    }
    export let trial: boolean
    /** Leave the wizard, to the page the user was going to land on after setup. */
    export let onFinish: () => void
    /** Back to the wizard's step before (its footer is hidden on this step: Previous is on the bottom row with Generate) */
    export let onPrevious: (() => void) | null = null

    // Same contexts the Program page is given by My Fitness
    const myFitness = new MyFitnessStores(data.user_params, data.training_advice, data.cal_settings, data.training_plan, false, false)
    setContext('myFitness', myFitness)
    setContext('medianAndWeather', { trainingMedians: writable(data.training_medians) })
    const { trainingAdvice } = myFitness

    const plan = writable(createGoalPlan(data.training_plan, data.training_advice, data.user_params, data.cal_settings))
    let forecastResults: any = null
    let scheduleWorkouts = true
    let savingResults = false
    let cancelForecast = () => {}
    let adapt = false

    // @ts-ignore
    const Xert = window.Xert
    // The plan was forecast with unlimited weekly hours: its Max Weekly Hours is the highest week it came to, within what the
    // setting allows. Rounded up to the hour, so the plan does not start out over its own limit: forecasting it again moves a
    // week by a few minutes, and a limit rounded down (7.3h to 7h) is then flagged as exceeded.
    function highestWeeklyHours(results: any) {
        const highest = Math.ceil(Math.max(...results.days.map((d: any) => d.weekly_hours ?? 0)))
        return Math.max(maxWeeklyHoursBounds.min, Math.min(maxWeeklyHoursBounds.max, highest))
    }

    let saveState: "idle" | "saving" | "saved" = "idle"

    async function savePlan() {
        if (saveState !== "idle") return
        saveState = "saving"
        const p = $plan
        const body = new FormData()
        body.append('payload', JSON.stringify({
            training_advice_as_of: $trainingAdvice.training_advice_as_of_val,
            focus: p.targetAthlete,
            settings: { ...p.settings, maxWeeklyHours: highestWeeklyHours(forecastResults) },
            cal_settings: p.cal_settings,
            program_type: "targetEvent",
            selected_mode: "target_focus",
            target_event_date: p.targetDate.toISOString(),
            specificity: p.specificity,
            result: forecastResults,
            schedule_workouts: scheduleWorkouts,
            focus_power: p.focusPower,
            goal_info: { targets_mode: p.targetsMode, manual_signature: null },
        }))

        // Workouts the plan couldn't get straight away are generated in the background
        const workoutsComplete = waitForForecastWorkouts()
        body.append('save_id', workoutsComplete.saveId)
        try {
            const response = await window.fetch(route('UserController@postTrainingProgram'), {
                ...baseRequest(),
                method: "POST",
                body,
            }).then(async r => {
                const result = await r.json().catch(() => ({}))
                // An expired session answers 401 with just a message, not result: false
                if (!r.ok) throw new Error(r.status === 401 || r.status === 419 ? "Your session has expired. Please log in again." : (result?.msg || result?.message))
                return result
            })
            if (response.result === false) throw new Error(response.msg)
            if (response.workouts_pending) await workoutsComplete.promise
            else workoutsComplete.cancel()
            saveState = "saved"
        } catch (e) {
            workoutsComplete.cancel()
            saveState = "idle"
            savingResults = false
            Xert.warning(e?.message || "We could not save your training plan. Please try again.")
        }
    }
</script>

<XfaiProgramPage
    firstTime
    formComponent={FirstTimeGoalForm}
    formProps={{ trial }}
    programType={ProgramType.Goal}
    {plan}
    bind:forecastResults
    bind:cancelForecast
    bind:adapt
    bind:savingResults
    bind:scheduleWorkouts
    onSave={savePlan}
    onSkip={onFinish}
    {onPrevious}
/>

{#if saveState !== "idle"}
    <CyclingProgress overlay listen={['progress']} done={saveState === "saved"}
        title="Building your training plan"
        messages={["Laying out your training days…", "Matching workouts to your goals…", "Pacing your training load…", "Filling your calendar…"]}
        doneTitle="Your training plan is ready!"
        doneMessage="Your first workouts are waiting for you."
    >
        <Button slot="done" size="lg" class="mt-2 px-10 text-lg" on:click={onFinish}>Let's get started!</Button>
    </CyclingProgress>
{/if}
